import { mm } from "@inovitas/infra3dsdk";
import {
  DrawLineStringTool,
  DrawPointTool,
  DrawPolygonTool,
  EditFeatureTool,
  SelectFeatureTool,
} from "@inovitas/infra3dsdk/dist/types/mm";
import { Feature } from "geojson";
import { create } from "zustand";

import { Tool } from "shared/ui/toolbar/tools/cursor/CursorContent";
import { FeatureTableAddedRows } from "shared/common/utils/layers/getChangedFeaturesList";
import { MeasurementType } from "shared/common/utils/measurements/measurementTools";

export interface Topics {
  addedFromUser: boolean;
  name: string;
  title?: string;
  folder: boolean;
  showOnMap: boolean;
  icon: string;
  topic?: string;
  layer?: mm.Layer;
  key?: string;
  expanded?: boolean;
  children?: Topics[];
}

export interface WebServiceLayerType {
  layers?: mm.Layer[];
  addedFromUser?: boolean;
  url?: string;
  type?: string;
  // To support the classic config
  source_options?: {
    //from Request init type
    body?: BodyInit | null;
    cache?: RequestCache;
    credentials?: RequestCredentials;
    headers?: HeadersInit;
    integrity?: string;
    keepalive?: boolean;
    method?: string;
    mode?: RequestMode;
    redirect?: RequestRedirect;
    referrer?: string;
    referrerPolicy?: ReferrerPolicy;
    signal?: AbortSignal | null;
    window?: null;
    //until here from Request init type
    servertype?: string;
    withCredentials?: boolean;
    token?: string;
    bearertoken?: string;
    generatetokenurl?: string;
    use_post?: boolean;
    xhr_mode?: string;
    xhr_headers?: string;
    fetch_options?: string;
    namespace?: string;
    version?: string;
    generatetoken?: { username: string; password: string };
    featureNS?: string;
  };
  project?: string;
  visible?: boolean;
  expanded?: boolean;
  name?: string;
  topic?: string;
}

export interface sidebarStoreInterface {
  closeAllToolPopover: () => void;
  imagePopover: boolean;
  changeImagePopover: (state: boolean) => void;
  axisControlPopover: boolean;
  changeAxisControlPopover: (state: boolean) => void;
  topicPopover: boolean;
  changeTopicPopover: (state: boolean) => void;
  infosPopover: boolean;
  changeInfosPopover: (state: boolean) => void;
  measurementPopover: boolean;
  changeMeasurementPopover: (state: boolean) => void;
  sharePopover: boolean;
  changeSharePopover: (state: boolean) => void;
  editPopover: boolean;
  changeEditPopover: (state: boolean) => void;
  cursorPopover: boolean;
  changeCursorPopover: (state: boolean) => void;
  maximizeTable: boolean;
  changeMaximizeTable: (state: boolean) => void;

  topics: Topics[];
  changeTopics: (state: Topics[]) => void;
  layerGroups: string[];
  changeLayerGroups: (state: string[]) => void;
  selectTools: SelectFeatureTool | undefined;
  changeSelectTools: (state: SelectFeatureTool | undefined) => void;
  measurementActiveTool: MeasurementType[];
  changeMeasurementActiveTool: (state: MeasurementType[]) => void;
  lastMeasurementActiveTool: MeasurementType[];
  changeLastMeasurementActiveTool: (state: MeasurementType[]) => void;

  showDescription: boolean;
  changeShowDescription: (state: boolean) => void;
  showEditToolsButtons: boolean;
  changeShowEditToolsButtons: (state: boolean) => void;
  showSmallToolContent: boolean;
  changeShowSmallToolContent: (state: boolean) => void;
  smallToolContent: MeasurementType | undefined;
  changeSmallToolContent: (state: MeasurementType | undefined) => void;
  distanceTool: any;
  changeDistanceTool: (state: any) => void;
  surveyWheelTool: any;
  changeSurveyWheelTool: (state: any) => void;
  profileTool: any;
  changeProfileTool: (state: any) => void;
  linrefTool: mm.DrawLRPointTool | undefined;
  changeLinrefTool: (state: mm.DrawLRPointTool | undefined) => void;
  distanceToolValues: any;
  changeDistanceToolValues: (state: any) => void;
  surveyWheelToolValues: any;
  changeSurveyWheelToolValues: (state: any) => void;
  distanceActiveAttributes: string[];
  changeDistanceActiveAttributes: (state: string[]) => void;
  surveyWheelActiveAttributes: string[];
  changeSurveyWheelActiveAttributes: (state: string[]) => void;
  verticalDistanceToolValues: any;
  changeVerticalDistanceToolValues: (state: any) => void;
  verticalDistanceActiveAttributes: string[];
  changeVerticalDistanceActiveAttributes: (state: string[]) => void;
  orthogonalDistanceTool: mm.OrthogonalMeasurementTool | undefined;
  changeOrthogonalDistanceTool: (
    state: mm.OrthogonalMeasurementTool | undefined
  ) => void;
  orthogonalDistanceToolValues: any;
  changeOrthogonalDistanceToolValues: (state: any) => void;
  orthogonalDistanceActiveAttributes: string[];
  changeOrthogonalDistanceActiveAttributes: (state: string[]) => void;
  areaToolValues: any;
  changeAreaToolValues: (state: any) => void;
  areaActiveAttributes: string[];
  changeAreaActiveAttributes: (state: string[]) => void;
  coordinateToolValues: any;
  changeCoordinateToolValues: (state: any) => void;
  coordinatesActiveAttributes: string[];
  changeCoordinatesActiveAttributes: (state: string[]) => void;
  linrefActiveAttributes: string[];
  changeLinrefActiveAttributes: (state: string[]) => void;
  profileToolValues: { x: number; y: number; z: number }[] | undefined;
  changeProfileToolValues: (
    state: { x: number; y: number; z: number }[] | undefined
  ) => void;
  linrefToolValues: mm.IUVW | null;
  changeLinrefToolValues: (state: mm.IUVW | null) => void;
  selectedTableRows: Feature | undefined;
  changeSelectedTableRows: (state: Feature | undefined) => void;
  editToolActivated: string;
  changeEditToolActivated: (state: string) => void;
  activeEditTool: boolean;
  changeActiveEditTool: (state: boolean) => void;
  activeCursorTool: Tool | undefined;
  changeActiveCursorTool: (state: Tool | undefined) => void;
  showToolDescription: boolean;
  changeShowToolDescription: (state: boolean) => void;
  editFeatureTableRow: Feature | undefined;
  changeEditFeatureTableRow: (state: Feature | undefined) => void;
  enableEditFeature: boolean;
  changeEnableEditFeature: (state: boolean) => void;
  enableDeleteFeature: boolean;
  changeEnableDeleteFeature: (state: boolean) => void;
  editMode: boolean;
  changeEditMode: (state: boolean) => void;
  editModeAddFeature:
    | DrawLineStringTool
    | DrawPolygonTool
    | DrawPointTool
    | undefined;
  changeEditModeAddFeature: (
    state: DrawLineStringTool | DrawPolygonTool | DrawPointTool | undefined
  ) => void;
  editModeMoveFeature: EditFeatureTool | undefined;
  changeEditModeMoveFeature: (state: EditFeatureTool | undefined) => void;
  editModeSelectFeature: SelectFeatureTool | undefined;
  changeEditModeSelectFeature: (state: SelectFeatureTool | undefined) => void;
  activeEditModeLayers: mm.Layer | null;
  changeActiveEditModeLayers: (state: mm.Layer | null) => void;
  featureTableAddedRows: FeatureTableAddedRows | undefined;
  changeFeatureTableAddedRows: (
    state: FeatureTableAddedRows | undefined
  ) => void;
  expandedTopics: string[];
  changeExpandedTopics: (state: string[]) => void;
  expandAddLayersList: boolean;
  changeExpandAddLayersList: (state: boolean) => void;
  activeAddLayersListItem: string;
  changeActiveAddLayersListItem: (state: string) => void;
  saveButtonStateOnAddNewLayers: boolean;
  changeSaveButtonStateOnAddNewLayers: (state: boolean) => void;
  newLayerGroupList: string[];
  changeNewLayerGroupList: (state: string[]) => void;
  newLayerProperties: any; // TODO: Types
  changeNewLayerProperties: (state: any) => void; // TODO: Types
  webServiceLayer: WebServiceLayerType;
  changeWebServiceLayer: (state: WebServiceLayerType) => void;
  webServiceLayerStep: number;
  changeWebServiceLayerStep: (state: number) => void;
  webServiceLayersList: mm.Layer[];
  changeWebServiceLayersList: (state: mm.Layer[]) => void;
  getUpdatedImagePopover: () => boolean;
  updateFeaturesList: boolean;
  changeUpdateFeaturesList: (state: boolean) => void;
  hoveredAxis: mm.Axis | null;
  changeHoveredAxis: (state: mm.Axis | null) => void;
  selectedAxis: mm.Axis | null;
  changeSelectedAxis: (state: mm.Axis | null) => void;
  axisPickTool: mm.SelectFeatureTool | undefined;
  changeAxisPickTool: (state: mm.SelectFeatureTool | undefined) => void;
}

export const sidebarStore = create<sidebarStoreInterface>()((set, get) => ({
  closeAllToolPopover: () => {
    set(() => ({
      topicPopover: false,
      axisControlPopover: false,
      measurementPopover: false,
      editPopover: false,
      imagePopover: false,
      maximizeTable: false,
      expandAddLayersList: false,
      showSmallToolContent: false,
      sharePopover: false,
    }));
  },
  imagePopover: false,
  changeImagePopover: (state: boolean) => set(() => ({ imagePopover: state })),

  topicPopover: false,
  changeTopicPopover: (state: boolean) => set(() => ({ topicPopover: state })),

  axisControlPopover: false,
  changeAxisControlPopover: (state: boolean) =>
    set(() => ({ axisControlPopover: state })),

  infosPopover: false,
  changeInfosPopover: (state: boolean) => set(() => ({ infosPopover: state })),

  measurementPopover: false,
  changeMeasurementPopover: (state: boolean) =>
    set(() => ({ measurementPopover: state })),

  sharePopover: false,
  changeSharePopover: (state: boolean) => set(() => ({ sharePopover: state })),

  editPopover: false,
  changeEditPopover: (state: boolean) => set(() => ({ editPopover: state })),

  cursorPopover: false,
  changeCursorPopover: (state: boolean) =>
    set(() => ({ cursorPopover: state })),

  maximizeTable: false,
  changeMaximizeTable: (state: boolean) =>
    set(() => ({ maximizeTable: state })),

  topics: [],
  changeTopics: (state: Topics[]) => set(() => ({ topics: state })),

  layerGroups: [],
  changeLayerGroups: (state: string[]) => set(() => ({ layerGroups: state })),

  selectTools: undefined,
  changeSelectTools: (state: SelectFeatureTool | undefined) =>
    set(() => ({ selectTools: state })),

  measurementActiveTool: [],
  changeMeasurementActiveTool: (state: MeasurementType[]) =>
    set(() => ({ measurementActiveTool: state })),

  lastMeasurementActiveTool: [],
  changeLastMeasurementActiveTool: (state: MeasurementType[]) =>
    set(() => ({ lastMeasurementActiveTool: state })),

  showDescription: false,
  changeShowDescription: (state: boolean) =>
    set(() => ({ showDescription: state })),

  showEditToolsButtons: true,
  changeShowEditToolsButtons: (state: boolean) =>
    set(() => ({ showEditToolsButtons: state })),

  showSmallToolContent: false,
  changeShowSmallToolContent: (state: boolean) =>
    set(() => ({ showSmallToolContent: state })),

  smallToolContent: undefined,
  changeSmallToolContent: (state: MeasurementType | undefined) =>
    set(() => ({ smallToolContent: state })),

  distanceTool: undefined,
  changeDistanceTool: (state: any) => set(() => ({ distanceTool: state })),

  surveyWheelTool: undefined,
  changeSurveyWheelTool: (state: any) =>
    set(() => ({ surveyWheelTool: state })),

  profileTool: undefined,
  changeProfileTool: (state: any) => set(() => ({ profileTool: state })),

  linrefTool: undefined,
  changeLinrefTool: (state: mm.DrawLRPointTool | undefined) =>
    set(() => ({ linrefTool: state })),

  distanceToolValues: undefined,
  changeDistanceToolValues: (state: any) =>
    set(() => ({ distanceToolValues: state })),

  surveyWheelToolValues: undefined,
  changeSurveyWheelToolValues: (state: any) =>
    set(() => ({ surveyWheelToolValues: state })),

  distanceActiveAttributes: ["slope_distance"],
  changeDistanceActiveAttributes: (state: string[]) =>
    set(() => ({ distanceActiveAttributes: state })),

  surveyWheelActiveAttributes: ["surveyWheel_distance"],
  changeSurveyWheelActiveAttributes: (state: string[]) =>
    set(() => ({ surveyWheelActiveAttributes: state })),

  verticalDistanceToolValues: undefined,
  changeVerticalDistanceToolValues: (state: any) =>
    set(() => ({ verticalDistanceToolValues: state })),

  verticalDistanceActiveAttributes: ["plumb_line"],
  changeVerticalDistanceActiveAttributes: (state: string[]) =>
    set(() => ({ verticalDistanceActiveAttributes: state })),

  orthogonalDistanceTool: undefined,
  changeOrthogonalDistanceTool: (
    state: mm.OrthogonalMeasurementTool | undefined
  ) => set(() => ({ orthogonalDistanceTool: state })),

  orthogonalDistanceToolValues: undefined,
  changeOrthogonalDistanceToolValues: (state: any) =>
    set(() => ({ orthogonalDistanceToolValues: state })),

  orthogonalDistanceActiveAttributes: ["distance"],
  changeOrthogonalDistanceActiveAttributes: (state: string[]) =>
    set(() => ({ orthogonalDistanceActiveAttributes: state })),

  areaToolValues: undefined,
  changeAreaToolValues: (state: any) => set(() => ({ areaToolValues: state })),

  areaActiveAttributes: ["area3d"],
  changeAreaActiveAttributes: (state: string[]) =>
    set(() => ({ areaActiveAttributes: state })),

  coordinateToolValues: undefined,
  changeCoordinateToolValues: (state: any) =>
    set(() => ({ coordinateToolValues: state })),

  coordinatesActiveAttributes: ["position"],
  changeCoordinatesActiveAttributes: (state: string[]) =>
    set(() => ({ coordinatesActiveAttributes: state })),

  linrefActiveAttributes: [],
  changeLinrefActiveAttributes: (state: string[]) =>
    set(() => ({ linrefActiveAttributes: state })),

  profileToolValues: undefined,
  changeProfileToolValues: (
    state: { x: number; y: number; z: number }[] | undefined
  ) => set(() => ({ profileToolValues: state })),

  linrefToolValues: null,
  changeLinrefToolValues: (state: mm.IUVW | null) =>
    set(() => ({ linrefToolValues: state })),

  selectedTableRows: undefined,
  changeSelectedTableRows: (state: Feature | undefined) =>
    set(() => ({ selectedTableRows: state })),

  editToolActivated: "",
  changeEditToolActivated: (state: string) =>
    set(() => ({ editToolActivated: state })),

  activeEditTool: false,
  changeActiveEditTool: (state: boolean) =>
    set(() => ({ activeEditTool: state })),

  activeCursorTool: undefined,
  changeActiveCursorTool: (state: Tool | undefined) =>
    set(() => ({ activeCursorTool: state })),

  showToolDescription: false,
  changeShowToolDescription: (state: boolean) =>
    set(() => ({ showToolDescription: state })),

  editFeatureTableRow: undefined,
  changeEditFeatureTableRow: (state: Feature | undefined) =>
    set(() => ({ editFeatureTableRow: state })),

  enableEditFeature: false,
  changeEnableEditFeature: (state: boolean) =>
    set(() => ({ enableEditFeature: state })),

  enableDeleteFeature: false,
  changeEnableDeleteFeature: (state: boolean) =>
    set(() => ({ enableDeleteFeature: state })),

  editMode: false,
  changeEditMode: (state: boolean) => set(() => ({ editMode: state })),

  editModeAddFeature: undefined,
  changeEditModeAddFeature: (
    state: DrawLineStringTool | DrawPolygonTool | DrawPointTool | undefined
  ) => set(() => ({ editModeAddFeature: state })),

  editModeMoveFeature: undefined,
  changeEditModeMoveFeature: (state: EditFeatureTool | undefined) =>
    set(() => ({ editModeMoveFeature: state })),

  editModeSelectFeature: undefined,
  changeEditModeSelectFeature: (state: SelectFeatureTool | undefined) =>
    set(() => ({ editModeSelectFeature: state })),

  activeEditModeLayers: null,
  changeActiveEditModeLayers: (state: mm.Layer | null) =>
    set(() => ({ activeEditModeLayers: state })),

  featureTableAddedRows: undefined,
  changeFeatureTableAddedRows: (state: FeatureTableAddedRows | undefined) =>
    set(() => ({ featureTableAddedRows: state })),

  expandedTopics: [],
  changeExpandedTopics: (state: string[]) =>
    set(() => ({ expandedTopics: state })),

  expandAddLayersList: false,
  changeExpandAddLayersList: (state: boolean) =>
    set(() => ({ expandAddLayersList: state })),

  activeAddLayersListItem: "",
  changeActiveAddLayersListItem: (state: string) =>
    set(() => ({ activeAddLayersListItem: state })),

  saveButtonStateOnAddNewLayers: false,
  changeSaveButtonStateOnAddNewLayers: (state: boolean) =>
    set(() => ({ saveButtonStateOnAddNewLayers: state })),

  newLayerGroupList: [""],
  changeNewLayerGroupList: (state: string[]) =>
    set(() => ({ newLayerGroupList: state })),

  newLayerProperties: undefined,
  changeNewLayerProperties: (
    state: any // TODO: Types
  ) => set(() => ({ newLayerProperties: state })),

  webServiceLayer: {},
  changeWebServiceLayer: (state: WebServiceLayerType) =>
    set(() => ({ webServiceLayer: state })),

  webServiceLayerStep: 0,
  changeWebServiceLayerStep: (state: number) =>
    set(() => ({ webServiceLayerStep: state })),

  webServiceLayersList: [],
  changeWebServiceLayersList: (state: mm.Layer[]) =>
    set(() => ({ webServiceLayersList: state })),

  getUpdatedImagePopover: () => {
    return get().imagePopover;
  },

  updateFeaturesList: false,
  changeUpdateFeaturesList: (state: boolean) => {
    set(() => ({ updateFeaturesList: state }));
    // always set it back to false after rerender IN-4060
    if (state) {
      setTimeout(() => set(() => ({ updateFeaturesList: false })), 0);
    }
  },

  hoveredAxis: null,
  changeHoveredAxis: (state: mm.Axis | null) =>
    set(() => ({ hoveredAxis: state })),

  selectedAxis: null,
  changeSelectedAxis: (state: mm.Axis | null) =>
    set(() => ({ selectedAxis: state })),

  axisPickTool: undefined,
  changeAxisPickTool: (state: mm.SelectFeatureTool | undefined) =>
    set(() => ({ axisPickTool: state })),
}));
